<script setup lang="ts">
import { computed, ref, toRef } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useTranslation } from "i18next-vue";
import { useGetDiagnostic, useUpdateDiagnostic } from "@/stores/queries";
import DiagnosticPageHeader from "@/components/diagnostics/diagnostic-page/DiagnosticPageHeader.vue";
import DiagnosticInspectionSection from "@/components/diagnostics/diagnostic-page/DiagnosticInspectionSection.vue";
import DiagnosticPlansSection from "@/components/diagnostics/diagnostic-page/DiagnosticPlansSection.vue";
import DiagnosticReportsSection from "@/components/diagnostics/diagnostic-page/DiagnosticReportsSection.vue";
import LinkReportDialog from "@/components/diagnostics/LinkReportDialog.vue";
import InputModal from "@/components/generic/InputModal.vue";

/**
 * Page d'un ouvrage, pleine largeur : en-tête de contexte, puis deux colonnes —
 * l'inspection terrain à gauche, les plans et rapports à droite. Sur petit
 * écran les colonnes s'empilent (inspection puis plans/rapports).
 */

const route = useRoute();
const router = useRouter();
const { t } = useTranslation();

const diagnosticId = computed(() => Number(route.params.id));
const diagnosticIdRef = toRef(diagnosticId);

const { data: diagnostic, isLoading } = useGetDiagnostic(diagnosticIdRef);
const { mutateAsync: updateDiagnostic, isPending: isUpdating } =
  useUpdateDiagnostic(diagnosticIdRef);

const save = (values: {
  gps_position?: string;
  access_instructions?: string;
}) => {
  updateDiagnostic(values);
};

// Modal de renommage (pilotée par ?edit=name, autofocus + présélection)
const showEditName = computed({
  get: () => route.query.edit === "name",
  set: (value) => {
    router.replace({ query: value ? { edit: "name" } : {} });
  },
});

const handleSaveName = async (name: string) => {
  if (name !== diagnostic.value?.name) {
    await updateDiagnostic({ name });
  }
  showEditName.value = false;
};

// Import du rapport de l'inspection précédente
const showImportReport = ref(false);

const goToProject = () => {
  if (diagnostic.value?.projectId) {
    router.push({
      name: "project",
      params: { id: diagnostic.value.projectId },
    });
  } else {
    router.push({ name: "projects" });
  }
};
</script>

<template>
  <div class="diagnostic-view">
    <div v-if="isLoading" class="loading-container">
      <div class="spinner-border" role="status">
        <span class="visually-hidden">Loading...</span>
      </div>
    </div>

    <template v-else-if="diagnostic">
      <DiagnosticPageHeader
        :diagnostic="diagnostic"
        @back="goToProject"
        @open-project="goToProject"
        @rename="showEditName = true"
        @import-report="showImportReport = true"
        @deleted="goToProject"
      />

      <main class="diagnostic-columns">
        <!-- Colonne gauche : préparation et inspection terrain -->
        <div class="column">
          <DiagnosticInspectionSection
            :diagnostic="diagnostic"
            @save="save($event)"
          />
        </div>

        <!-- Colonne droite : plans puis rapports -->
        <div class="column">
          <DiagnosticPlansSection
            :diagnostic-id="diagnostic.id"
            :project-id="diagnostic.projectId"
          />
          <DiagnosticReportsSection :diagnostic="diagnostic" />
        </div>
      </main>

      <LinkReportDialog
        v-model="showImportReport"
        :diagnostic-id="diagnostic.id"
        :project-id="diagnostic.projectId"
      />
    </template>

    <!-- Renommage -->
    <InputModal
      v-model="showEditName"
      :title="t('report:diagnostics.editName.title')"
      :value="diagnostic?.name"
      :placeholder="t('report:diagnostics.editName.placeholder')"
      :loading="isUpdating"
      @confirm="handleSaveName"
    />
  </div>
</template>

<style lang="scss" scoped>
.diagnostic-view {
  min-height: 100%;
  width: 100%;
}

.loading-container {
  display: flex;
  justify-content: center;
  padding: 48px;
}

.diagnostic-columns {
  display: grid;
  grid-template-columns: 1fr 1fr;
  align-items: start;
  gap: 40px;
  padding: 28px 20px 48px;
}

.column {
  display: flex;
  flex-direction: column;
  gap: 32px;
  min-width: 0;
}

/* En dessous de lg, deux colonnes deviennent trop étroites une fois la barre
   latérale déduite (~240px) → on empile inspection puis plans/rapports. */
@media (max-width: 1199.98px) {
  .diagnostic-columns {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 767.98px) {
  .diagnostic-columns {
    padding: 20px 12px 24px;
  }
}
</style>
